// The share image: the player's sky drawn to a canvas as the board shows it,
// the weekday's gradient, the background stars, the constellations' lines
// and atlas stars, the planet, without the word pills, and a slim caption strip below
// with the play's stats. Unused stars are plain points of light, so the
// image carries no letters. 1600 px wide; a sky wider than the 16:9 window
// around its stars is fitted in whole, with more night above and below.
import { derive, GLYPH_UNITS, PLANET_COLORS, planetAppearance, planetDiamondPath, planetPath, planetsOf, SKY_HEIGHT, skyWidth, type PlayLevel, type PlayState } from '@wits/core';
import { glyphScale } from '../../sky/viewport';
import { backgroundStars, HALF_HEIGHT, WEEKDAY_SKIES, weekdayFromDate } from '../../sky/weekdaySkies';

export const IMAGE_WIDTH = 1600;
export const IMAGE_SKY_HEIGHT = 900;
export const IMAGE_CAPTION_HEIGHT = 64;
export const IMAGE_HEIGHT = IMAGE_SKY_HEIGHT + IMAGE_CAPTION_HEIGHT;

/** Sky units kept clear around the outermost stars. */
const MARGIN = 160;
const COLORS = {
  star: '#fff4d6',
  starQuiet: 'rgba(255, 244, 214, 0.62)',
  starFaint: 'rgba(255, 244, 214, 0.3)',
  cool: '#dce8ff',
  line: '#8fa3b8',
  giltHairline: 'rgba(201, 162, 39, 0.6)',
  ink: '#0b1826',
};

interface Input {
  level: PlayLevel;
  board: PlayState;
  /** The play's date, for its weekday sky. */
  date: string;
  /** The caption's stats, left to right. */
  caption: string[];
}

export async function renderSkyImage({ level, board, date, caption }: Input): Promise<Blob> {
  await Promise.all(['italic 500 26px Fraunces', '500 17px "IBM Plex Mono"'].map((f) => document.fonts.load(f).catch(() => [])));
  const canvas = document.createElement('canvas');
  canvas.width = IMAGE_WIDTH;
  canvas.height = IMAGE_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas');

  // The window: 16:9 around the level's nodes, or wider to take them all in;
  // a sky narrower than 16:9 (1:1, 4:3) sits centered in it, night either side.
  const skyW = skyWidth(level.sky.preset);
  const xs = [...level.nodes, ...planetsOf(level)].map((n) => n.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const regionW = Math.max((SKY_HEIGHT * IMAGE_WIDTH) / IMAGE_SKY_HEIGHT, Math.min(skyW, maxX - minX + 2 * MARGIN));
  const x0 = skyW <= regionW ? (skyW - regionW) / 2 : Math.min(Math.max((minX + maxX) / 2 - regionW / 2, 0), skyW - regionW);
  const scale = IMAGE_WIDTH / regionW;
  const oy = (IMAGE_SKY_HEIGHT - SKY_HEIGHT * scale) / 2;
  const at = (n: { x: number; y: number }) => ({ x: (n.x - x0) * scale, y: oy + n.y * scale });

  // The weekday's night: the play half runs from the top color to the middle one.
  const sky = WEEKDAY_SKIES[weekdayFromDate(date)];
  const gradient = ctx.createLinearGradient(0, oy, 0, oy + SKY_HEIGHT * scale);
  gradient.addColorStop(0, sky.top);
  gradient.addColorStop(1, sky.mid);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, IMAGE_WIDTH, IMAGE_SKY_HEIGHT);

  // Background stars keep their screen size and density, as in play: drawn
  // at a 900 px screen's scale whatever the level's fit.
  const bgScale = IMAGE_SKY_HEIGHT / HALF_HEIGHT;
  for (const s of backgroundStars(weekdayFromDate(date))) {
    if (s.y >= HALF_HEIGHT || s.x * bgScale > IMAGE_WIDTH) continue;
    ctx.globalAlpha = s.opacity;
    ctx.fillStyle = s.cool ? COLORS.cool : COLORS.star;
    ctx.beginPath();
    ctx.arc(s.x * bgScale, s.y * bgScale, s.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  const unit = glyphScale(scale, GLYPH_UNITS) * scale;
  const nodes = new Map(level.nodes.map((n) => [n.id, n]));
  const used = new Set(board.constellations.flatMap((c) => c.nodes));

  ctx.strokeStyle = COLORS.line;
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1.5, 6 * scale);
  for (const c of board.constellations) {
    for (const [a, b] of c.lines) {
      const pa = at(nodes.get(a)!);
      const pb = at(nodes.get(b)!);
      ctx.beginPath();
      ctx.moveTo(pa.x, pa.y);
      ctx.lineTo(pb.x, pb.y);
      ctx.stroke();
    }
  }

  for (const node of level.nodes) {
    const p = at(node);
    if (used.has(node.id)) {
      glow(ctx, p.x, p.y, 70 * unit, 0.22);
      fourPointStar(ctx, p.x, p.y, 26 * unit, 4.5 * unit, COLORS.star);
    } else {
      glow(ctx, p.x, p.y, 24 * unit, 0.18);
      ctx.fillStyle = COLORS.star;
      ctx.globalAlpha = 0.75;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(1.6, 6 * unit), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // The planet, in its diamond until a constellation ringed it.
  const { planetsActivated } = derive(board);
  for (const planet of planetsOf(level)) {
    const p = at(planet);
    const look = planetAppearance(level, planet);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.scale(unit, unit);
    if (!planetsActivated.has(planet.id)) {
      ctx.strokeStyle = PLANET_COLORS[look.kind];
      ctx.lineWidth = 3;
      ctx.stroke(new Path2D(planetDiamondPath()));
    }
    ctx.fillStyle = PLANET_COLORS[look.kind];
    ctx.fill(new Path2D(planetPath(look)), 'evenodd');
    ctx.restore();
  }

  drawCaption(ctx, caption);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('No image'))), 'image/png'));
}

function drawCaption(ctx: CanvasRenderingContext2D, caption: string[]): void {
  const top = IMAGE_SKY_HEIGHT;
  const mid = top + IMAGE_CAPTION_HEIGHT / 2;
  ctx.fillStyle = COLORS.ink;
  ctx.fillRect(0, top, IMAGE_WIDTH, IMAGE_CAPTION_HEIGHT);
  ctx.fillStyle = COLORS.giltHairline;
  ctx.fillRect(0, top, IMAGE_WIDTH, 1.5);

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.star;
  ctx.font = 'italic 500 26px Fraunces, Georgia, serif';
  const title = 'Written in the Stars';
  ctx.fillText(title, 32, mid);
  const titleWidth = ctx.measureText(title).width;

  // The stats in mono caps, shrinking to fit beside the title.
  const text = caption.map((part) => part.toUpperCase()).join('  ·  ');
  const room = IMAGE_WIDTH - 64 - titleWidth - 48;
  const spaced = 'letterSpacing' in ctx;
  let size = 17;
  for (; size > 11; size--) {
    ctx.font = `500 ${size}px "IBM Plex Mono", Menlo, monospace`;
    if (spaced) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${(size * 0.12).toFixed(1)}px`;
    if (ctx.measureText(text).width <= room) break;
  }
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.starQuiet;
  ctx.fillText(text, IMAGE_WIDTH - 32, mid + 1);
  if (spaced) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = '0px';
}

/** A soft halo of star white. */
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, alpha: number): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(255, 244, 214, ${alpha})`);
  g.addColorStop(1, 'rgba(255, 244, 214, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

/** The atlas star symbol, `o` to the tips and `i` to the inner corners. */
function fourPointStar(ctx: CanvasRenderingContext2D, x: number, y: number, o: number, i: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - o);
  ctx.lineTo(x + i, y - i);
  ctx.lineTo(x + o, y);
  ctx.lineTo(x + i, y + i);
  ctx.lineTo(x, y + o);
  ctx.lineTo(x - i, y + i);
  ctx.lineTo(x - o, y);
  ctx.lineTo(x - i, y - i);
  ctx.closePath();
  ctx.fill();
}
